/**
 * Group Invites List Component
 * Displays and manages pending group invitations for the current user
 */

import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Avatar,
  Typography,
  Alert,
  CircularProgress,
  Tooltip,
  Button,
} from '@mui/material'
import {
  Groups as GroupsIcon,
  Check as AcceptIcon,
  Close as DeclineIcon,
  Mail as MailIcon,
} from '@mui/icons-material'
import { socialApi } from '@api/endpoints/social'

const GROUP_ICON_COLORS = [
  { bg: 'rgba(66,153,225,0.12)', color: '#4299e1' },
  { bg: 'rgba(56,178,172,0.12)', color: '#38b2ac' },
  { bg: 'rgba(212,168,83,0.12)', color: '#d4a853' },
  { bg: 'rgba(159,122,234,0.12)', color: '#9f7aea' },
  { bg: 'rgba(72,187,120,0.12)', color: '#48bb78' },
]

function getGroupIconColor(name: string) {
  let hash = 0
  for (let i = 0; i < (name || '').length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return GROUP_ICON_COLORS[Math.abs(hash) % GROUP_ICON_COLORS.length]
}

export function GroupInvitesList() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: invites = [], isLoading, error } = useQuery({
    queryKey: ['group-invites'],
    queryFn: socialApi.getMyGroupInvites,
  })

  const acceptMutation = useMutation({
    mutationFn: (groupId: string) => socialApi.acceptGroupInvite(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-invites'] })
      queryClient.invalidateQueries({ queryKey: ['my-groups'] })
    },
  })

  const declineMutation = useMutation({
    mutationFn: (groupId: string) => socialApi.declineGroupInvite(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-invites'] })
    },
  })

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error">
        {error instanceof Error ? error.message : 'Failed to load group invites'}
      </Alert>
    )
  }

  if (!invites || invites.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, opacity: 0.5 }}>
        <MailIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body1" color="text.secondary">
          No Invites
        </Typography>
        <Typography variant="body2" color="text.secondary">
          When groups invite you, they'll appear here.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {invites.map((invite: any) => {
        const groupName = invite.groupName || invite.name || 'Unknown Group'
        const groupId = invite.groupId || invite.id
        const description = invite.groupDescription || invite.description || ''
        const iconColor = getGroupIconColor(groupName)

        return (
          <Box
            key={groupId}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: '14px',
              p: '12px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
            }}
          >
            <Avatar
              variant="rounded"
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                bgcolor: iconColor.bg,
                color: iconColor.color,
                fontSize: 20,
                flexShrink: 0,
              }}
            >
              <GroupsIcon />
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {groupName}
              </Typography>
              {description && (
                <Typography
                  sx={{
                    fontSize: 11,
                    color: 'text.secondary',
                    mt: 0.25,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {description}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              <Tooltip title="Accept">
                <Button
                  size="small"
                  onClick={() => acceptMutation.mutate(groupId)}
                  disabled={acceptMutation.isPending}
                  sx={{
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '10px',
                    fontSize: 12,
                    fontWeight: 600,
                    bgcolor: 'rgba(72,187,120,0.12)',
                    border: '1px solid rgba(72,187,120,0.25)',
                    color: 'success.main',
                    '&:hover': { bgcolor: 'rgba(72,187,120,0.2)' },
                  }}
                >
                  <AcceptIcon sx={{ fontSize: 16 }} />
                </Button>
              </Tooltip>
              <Tooltip title="Decline">
                <Button
                  size="small"
                  onClick={() => declineMutation.mutate(groupId)}
                  disabled={declineMutation.isPending}
                  sx={{
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '10px',
                    fontSize: 12,
                    fontWeight: 600,
                    bgcolor: 'rgba(252,129,129,0.08)',
                    border: '1px solid rgba(252,129,129,0.15)',
                    color: 'error.main',
                    '&:hover': { bgcolor: 'rgba(252,129,129,0.15)' },
                  }}
                >
                  <DeclineIcon sx={{ fontSize: 16 }} />
                </Button>
              </Tooltip>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}
